import moment from 'moment';
import 'moment/locale/fr';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import user from '../../assets/user.png';
import { useAxios } from '../../utils/hook/useAxios';

moment.locale('fr');

const STATUS_MAP = {
  pending: { label: 'En attente', bg: 'bg-warning text-dark' },
  accepted: { label: 'Acceptée', bg: 'bg-primary text-white' },
  paid: { label: 'Confirmée', bg: 'bg-success text-white' },
  completed: { label: 'Terminée', bg: 'bg-success text-white' },
  rejected: { label: 'Annulée', bg: 'bg-danger text-white' },
};

function ConversationRow({ data }) {
  const location = useLocation();
  const isActive = location.pathname === '/inbox/messages/thread/' + data.reservationId;
  const s = STATUS_MAP[data.status] ?? { label: data.status, bg: 'bg-secondary text-white' };

  return (
    <Link to={'/inbox/messages/thread/' + data.reservationId} className="text-decoration-none">
      <div
        className={`d-flex align-items-center gap-3 px-3 py-3 border-bottom ${isActive ? 'bg-primary bg-opacity-10' : 'bg-white'}`}
        style={{ transition: 'background 0.15s' }}
      >
        {/* Avatar */}
        <div className="flex-shrink-0 position-relative">
          <img
            src={data.Invite?.profile_url || user}
            alt="avatar"
            style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }}
          />
          {data.status === 'pending' && (
            <span
              className="position-absolute bottom-0 end-0 bg-warning border border-white rounded-circle"
              style={{ width: 12, height: 12 }}
            />
          )}
        </div>

        {/* Infos */}
        <div className="flex-grow-1 overflow-hidden">
          <div className="d-flex justify-content-between align-items-center">
            <p className="mb-0 fw-semibold text-truncate" style={{ fontSize: 14 }}>
              {data.Invite?.firstName} {data.Invite?.lastName}
            </p>
            <small className="text-muted flex-shrink-0 ms-2" style={{ fontSize: 11 }}>
              {moment(data.updatedAt).fromNow()}
            </small>
          </div>
          <p className="mb-0 text-muted text-truncate" style={{ fontSize: 12 }}>
            <i className="bi bi-car-front me-1"></i>
            {data.Vehicle?.VehicleModel?.marque} {data.Vehicle?.VehicleModel?.modele}
          </p>
          <div className="mt-1">
            <span className={`badge rounded-pill ${s.bg}`} style={{ fontSize: 10 }}>
              {s.label}
            </span>
            <span className="text-muted ms-2" style={{ fontSize: 11 }}>
              #{data.reservationId}
            </span>
          </div>
        </div>

        <i className="bi bi-chevron-right text-muted flex-shrink-0" style={{ fontSize: 12 }} />
      </div>
    </Link>
  );
}

function SkeletonRow() {
  return (
    <div className="d-flex align-items-center gap-3 px-3 py-3 border-bottom bg-white">
      <div
        className="rounded-circle bg-secondary bg-opacity-25 flex-shrink-0"
        style={{ width: 48, height: 48 }}
      />
      <div className="flex-grow-1">
        <div
          className="bg-secondary bg-opacity-25 rounded mb-2"
          style={{ height: 12, width: '50%' }}
        />
        <div className="bg-secondary bg-opacity-25 rounded" style={{ height: 10, width: '70%' }} />
      </div>
    </div>
  );
}

function InboxListPage() {
  const axios = useAxios();
  const userData = JSON.parse(window.localStorage.getItem('me') || 'null');

  const [reservations, setReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!userData) return;
    axios
      .get('/reservations/getbydriverid/' + userData.id)
      .then(({ data }) => {
        if (Array.isArray(data)) {
          setReservations(data.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
        }
      })
      .catch((e) => console.error(e))
      .finally(() => setIsLoading(false));
  }, []);

  const FILTERS = [
    { key: 'all', label: 'Tous' },
    { key: 'pending', label: 'En attente' },
    { key: 'accepted', label: 'Acceptées' },
    { key: 'paid', label: 'Confirmées' },
  ];

  const filtered =
    filter === 'all' ? reservations : reservations.filter((r) => r.status === filter);

  const pendingCount = reservations.filter((r) => r.status === 'pending').length;

  return (
    <div className="bg-body-tertiary min-vh-100">
      {/* Header sticky */}
      <div className="bg-white border-bottom" style={{ position: 'sticky', top: 0, zIndex: 100 }}>
        <div className="px-3 pt-3 pb-2">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h5 className="mb-0 fw-bold">
              Messages
              {pendingCount > 0 && (
                <span className="badge bg-danger rounded-pill ms-2" style={{ fontSize: 11 }}>
                  {pendingCount}
                </span>
              )}
            </h5>
          </div>

          {/* Filtres */}
          <div className="d-flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {FILTERS.map(({ key, label }) => (
              <button
                key={key}
                className={`btn btn-sm rounded-pill flex-shrink-0 ${filter === key ? 'btn-primary' : 'btn-outline-secondary'}`}
                style={{ fontSize: 12 }}
                onClick={() => setFilter(key)}
              >
                {label}
                {key === 'pending' && pendingCount > 0 && (
                  <span className="ms-1 badge bg-danger rounded-pill" style={{ fontSize: 9 }}>
                    {pendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
        ) : filtered.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-chat-dots" style={{ fontSize: 40 }}></i>
            <p className="mt-3 mb-0">Aucune conversation trouvée</p>
          </div>
        ) : (
          filtered.map((r) => <ConversationRow key={r.reservationId} data={r} />)
        )}
      </div>
    </div>
  );
}

export default InboxListPage;
